import { useState, useEffect } from "react";
import { API } from "@/App";
import { Pulse } from "@phosphor-icons/react";

const BOT_COLORS = { scalp: "#FF7F6E", day: "#378ADD", swing: "#14B8A6" };

// Returns true if current CET time is within Frankfurt cash session (08-22 CET)
const isMarketOpen = () => {
  const now = new Date();
  // Convert to CET: Europe/Berlin offset
  const cet = new Date(now.toLocaleString("en-US", { timeZone: "Europe/Berlin" }));
  const hour = cet.getHours();
  const day = cet.getDay();
  if (day === 0 || day === 6) return false; // weekend
  return hour >= 8 && hour < 22;
};

const fmtCET = (date) => {
  return new Intl.DateTimeFormat("fr-FR", {
    timeZone: "Europe/Berlin",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);
};

export default function DaxLiveHeader() {
  // Live clock
  const [now, setNow] = useState(new Date());
  // Mock-real-time DAX price (drifts ±0.1% every 5s)
  const [daxPrice, setDaxPrice] = useState(18280.5);
  const [priceTrend, setPriceTrend] = useState(0);
  // Bot statuses (fetched from /api/bots/configs)
  const [botStatuses, setBotStatuses] = useState({ scalp: false, day: false, swing: false });

  useEffect(() => {
    const clockInt = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(clockInt);
  }, []);

  useEffect(() => {
    const priceInt = setInterval(() => {
      setDaxPrice(prev => {
        const drift = prev * (Math.random() - 0.5) * 0.002;  // ±0.1% per tick
        const next = +(prev + drift).toFixed(1);
        setPriceTrend(drift);
        return next;
      });
    }, 5000);
    return () => clearInterval(priceInt);
  }, []);

  useEffect(() => {
    const fetchBots = async () => {
      try {
        const res = await fetch(`${API}/bots/configs`).then(r => r.json());
        const map = {};
        res.forEach(c => {
          // backend uses bot_id "daytrade", we map to "day"
          const key = c.bot_id === "daytrade" ? "day" : c.bot_id;
          map[key] = !!c.enabled;
        });
        setBotStatuses(map);
      } catch {
        // ignore
      }
    };
    fetchBots();
    const int = setInterval(fetchBots, 30000); // refresh every 30s
    return () => clearInterval(int);
  }, []);

  const open = isMarketOpen();
  const trendColor = priceTrend > 0 ? "#1D9E75" : priceTrend < 0 ? "#D85A30" : "#64748b";

  return (
    <div
      className="bg-slate-900 text-white rounded-sm mb-6 px-4 py-3 flex items-center justify-between flex-wrap gap-3"
      data-testid="dax-live-header"
    >
      {/* Left: app name + clock */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Pulse size={18} weight="fill" className="text-[#1D9E75] animate-pulse" />
          <span className="font-heading text-base font-bold tracking-wider">DAX PRO SYSTEM</span>
        </div>
        <div className="hidden md:flex items-center gap-1 pl-4 border-l border-slate-700">
          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-body">CET</span>
          <span className="font-data text-sm tabular-nums text-slate-200" data-testid="header-clock">
            {fmtCET(now)}
          </span>
        </div>
      </div>

      {/* Center: DAX live price */}
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-body">DAX40</span>
        <span
          className="font-data text-lg font-bold tabular-nums transition-colors duration-500"
          style={{ color: trendColor }}
          data-testid="header-dax-price"
        >
          {daxPrice.toLocaleString("fr-FR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
        </span>
        <span className="text-[10px] font-data" style={{ color: trendColor }}>
          {priceTrend >= 0 ? "▲" : "▼"} {Math.abs(priceTrend).toFixed(1)}
        </span>
      </div>

      {/* Right: market status + bot indicators */}
      <div className="flex items-center gap-3">
        <div
          className={`px-2.5 py-1 rounded-sm text-[10px] uppercase tracking-wider font-bold ${
            open ? "bg-[#1D9E75] text-white" : "bg-slate-700 text-slate-400"
          }`}
          data-testid="header-market-status"
        >
          {open ? "● OUVERT" : "○ FERMÉ"}
        </div>
        <div className="flex items-center gap-1.5">
          {[
            { id: "scalp", label: "S" },
            { id: "day", label: "D" },
            { id: "swing", label: "W" },
          ].map(b => (
            <div
              key={b.id}
              className="flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-data font-bold"
              style={{
                background: botStatuses[b.id] ? `${BOT_COLORS[b.id]}30` : "rgba(100, 116, 139, 0.2)",
                color: botStatuses[b.id] ? BOT_COLORS[b.id] : "#94a3b8",
                border: `1px solid ${botStatuses[b.id] ? BOT_COLORS[b.id] : "#475569"}`,
              }}
              title={`Bot ${b.id}: ${botStatuses[b.id] ? "ACTIVE" : "PAUSED"}`}
              data-testid={`header-bot-${b.id}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${botStatuses[b.id] ? "bg-current animate-pulse" : "bg-current opacity-40"}`} />
              {b.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
